/**
 * Siamang API Client
 * 
 * All communication with the backend goes through this module.
 * The frontend NEVER talks to Supabase tables directly.
 * 
 * Auth tokens are obtained from Supabase Auth (the only thing
 * the frontend uses Supabase for) and passed to the Cloudflare Worker.
 */

import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_WORKER_URL || 'https://kagi-monitoring.workers.dev';

// ─── Core request helper ─────────────────────────────────────────────────────

// Cache the auth headers briefly so parallel calls don't each hit getSession()
let _cachedHeaders = null;
let _cachedAt = 0;
const HEADER_CACHE_MS = 5000; // 5 seconds

async function getAuthHeaders() {
  if (_cachedHeaders && Date.now() - _cachedAt < HEADER_CACHE_MS) {
    return _cachedHeaders;
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  _cachedHeaders = {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json'
  };
  _cachedAt = Date.now();
  return _cachedHeaders;
}

export async function request(path, options = {}) {
  const headers = options.headers || await getAuthHeaders();
  const url = `${API_URL}${path}`;

  const response = await fetch(url, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || `API error ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}


// ─── Company ─────────────────────────────────────────────────────────────────

export async function getCompany() {
  return request('/api/company');
}

export async function createCompany({ name, size }) {
  return request('/api/company', {
    method: 'POST',
    body: { name, size }
  });
}


// ─── Domains ─────────────────────────────────────────────────────────────────

export async function getDomains() {
  return request('/api/domains');
}

export async function addDomain({ domain_name, verification_method }) {
  return request('/api/domains', {
    method: 'POST',
    body: { domain_name, verification_method }
  });
}

export async function verifyDomain({ domain, token, method }) {
  return request('/api/verify-domain', {
    method: 'POST',
    body: { domain, token, method }
  });
}


// ─── Services ────────────────────────────────────────────────────────────────

export async function getServices() {
  return request('/api/services');
}

export async function upsertService({ service_type, tier, onboarding_status }) {
  return request('/api/services', {
    method: 'POST',
    body: { service_type, tier, onboarding_status }
  });
}


// ─── Identity Config ─────────────────────────────────────────────────────────

export async function upsertIdentityConfig({ platform, config_data }) {
  return request('/api/identity-config', {
    method: 'POST',
    body: { platform, config_data }
  });
}

export async function getIamPlaygroundConfigs() {
  return request('/api/iam-playground/configs');
}

export async function testIamPlaygroundConfig(configId) {
  return request('/api/iam-playground/test/' + configId, {
    method: 'POST'
  });
}


// ─── Monitoring Config ───────────────────────────────────────────────────────

export async function getMonitoringConfig(domain) {
  return request(`/api/monitoring-config?domain=${encodeURIComponent(domain)}`);
}

export async function saveMonitoringConfig(domain, config) {
  return request('/api/monitoring-config', {
    method: 'POST',
    body: { domain, config }
  });
}

export async function getMonitoringLastRun(domain) {
  return request(`/api/monitoring-lastrun?domain=${encodeURIComponent(domain)}`);
}


// ─── Monitoring Checks ───────────────────────────────────────────────────────

export async function runCheck(checkType, domain) {
  return request(`/api/${checkType}?domain=${encodeURIComponent(domain)}`);
}

export async function runAllChecks(domain) {
  const checks = ['ssl', 'email-auth', 'reputation', 'uptime', 'breach', 'ct', 'cdn'];
  const results = await Promise.allSettled(
    checks.map(check => runCheck(check, domain))
  );

  const data = {};
  checks.forEach((check, i) => {
    data[check] = results[i].status === 'fulfilled' ? results[i].value : null;
  });
  return data;
}

export default {
  getCompany, createCompany,
  getDomains, addDomain, verifyDomain,
  getServices, upsertService,
  upsertIdentityConfig,
  getMonitoringConfig, saveMonitoringConfig,
  getMonitoringLastRun,
  runCheck, runAllChecks
};
